import {
  Badge,
  Box,
  Checkbox,
  IconButton,
  SxProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { Message } from "@mui/icons-material";
import SellIcon from "@mui/icons-material/Sell";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { DateTime } from "luxon";
import { ReactNode } from "react";
import { StickyStyle } from "../Common/styles/table";
import StatusBadge from "../Common/UI/StatusTag";
import { getFrozenCellSx } from "../../hooks/useStickyColumnOffsets";
import {
  RegistryFilterContext,
  renderRegistryColumnHeader,
} from "../../utils/registryColumnFilters";

export type RegistryParticipantColumnDef = {
  key: string;
  label: string;
  freezable: boolean;
  minWidth?: number;
  stickyLeftAlways?: boolean;
  padding?: "checkbox" | "normal";
  renderHeader: () => ReactNode;
  renderCell: (row: any) => ReactNode;
};

const statusMap: Record<string, { label: string; variant: string }> = {
  pending: { label: "Pending", variant: "pending" },
  screened: { label: "Screened", variant: "active" },
};

const redactionStatusMap: Record<
  string,
  { label: string; variant: "active" | "pending" | "cancelled" | "completed" }
> = {
  active: { label: "Active", variant: "active" },
  deactivated: { label: "Deactivated", variant: "pending" },
  redacted: { label: "Redacted", variant: "cancelled" },
  deleted: { label: "Deleted", variant: "completed" },
};

type BuildRegistryColumnsParams = {
  currentPageIds: string[];
  selectedIds: Set<string>;
  isAllPageSelected: boolean;
  handleSelectAllPage: (checked: boolean) => void;
  handleToggleRow: (participantId: string) => void;
  handleClick: (row: any) => void;
  copiedCode: string | null;
  setCopiedCode: (code: string | null) => void;
  handleShowTags: (tags: string[]) => void;
  handleShowNoteDetails: (row: any) => void;
  handleShowStudyDetails: (row: any) => void;
  setHistoryTarget: (target: {
    participantId: string;
    status: "active" | "deactivated" | "redacted" | "deleted";
  }) => void;
  filterContext?: RegistryFilterContext;
};

export function buildRegistryParticipantColumns({
  currentPageIds,
  selectedIds,
  isAllPageSelected,
  handleSelectAllPage,
  handleToggleRow,
  handleClick,
  copiedCode,
  setCopiedCode,
  handleShowTags,
  handleShowNoteDetails,
  handleShowStudyDetails,
  setHistoryTarget,
  filterContext,
}: BuildRegistryColumnsParams): RegistryParticipantColumnDef[] {
  return [
    {
      key: "select",
      label: "Select",
      freezable: false,
      stickyLeftAlways: true,
      padding: "checkbox",
      renderHeader: () => (
        <Checkbox
          indeterminate={
            currentPageIds.some((id) => selectedIds.has(id)) &&
            !isAllPageSelected
          }
          checked={isAllPageSelected}
          onChange={(e) => handleSelectAllPage(e.target.checked)}
        />
      ),
      renderCell: (row) => (
        <Checkbox
          checked={selectedIds.has(row.participantId)}
          onChange={() => handleToggleRow(row.participantId)}
        />
      ),
    },
    {
      key: "systemId",
      label: "System ID",
      freezable: true,
      minWidth: 120,
      renderHeader: () =>
        renderRegistryColumnHeader("systemId", "System ID", filterContext),
      renderCell: (row) =>
        row?.participantCode ? (
          <Tooltip
            title={copiedCode === row.participantCode ? "Copied!" : "Click to copy"}
            arrow
          >
            <Typography
              variant="body2"
              fontFamily="monospace"
              sx={{ cursor: "pointer", userSelect: "none" }}
              onClick={() => {
                navigator.clipboard.writeText(row.participantCode);
                setCopiedCode(row.participantCode);
                setTimeout(() => setCopiedCode(null), 1500);
              }}
            >
              {row.participantCode}
            </Typography>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      key: "email",
      label: "Email",
      freezable: true,
      renderHeader: () => "Email",
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography fontWeight="regular" variant="body1" sx={{ flexShrink: 0 }}>
            {row?.email || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      key: "name",
      label: "Name",
      freezable: true,
      minWidth: 150,
      renderHeader: () => "Name",
      renderCell: (row) => row?.name || "-",
    },
    {
      key: "phoneNumber",
      label: "Phone Number",
      freezable: true,
      minWidth: 130,
      renderHeader: () => "Phone Number",
      renderCell: (row) => row?.phoneNumber || "-",
    },
    {
      key: "status",
      label: "Status",
      freezable: true,
      renderHeader: () =>
        renderRegistryColumnHeader("status", "Status", filterContext),
      renderCell: (row) => (
        <StatusBadge
          variant={statusMap[row?.status]?.variant as any}
          label={statusMap[row?.status]?.label ?? row?.status}
        />
      ),
    },
    {
      key: "redactionStatus",
      label: "Redaction Status",
      freezable: true,
      minWidth: 140,
      renderHeader: () =>
        renderRegistryColumnHeader(
          "redactionStatus",
          "Redaction Status",
          filterContext,
        ),
      renderCell: (row) =>
        row?.redactionStatus ? (
          <Tooltip arrow title="Click to view redaction history (if any)">
            <Box
              component="span"
              sx={{ display: "inline-block", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                setHistoryTarget({
                  participantId: row.participantId,
                  status: row.redactionStatus,
                });
              }}
            >
              <StatusBadge
                variant={redactionStatusMap[row.redactionStatus]?.variant}
                label={
                  redactionStatusMap[row.redactionStatus]?.label ??
                  row.redactionStatus
                }
              />
            </Box>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      key: "screenerCompletedDate",
      label: "Screener Completion Date",
      freezable: true,
      minWidth: 260,
      renderHeader: () =>
        renderRegistryColumnHeader(
          "screenerCompletedDate",
          "Screener Completion Date",
          filterContext,
        ),
      renderCell: (row) =>
        row?.screenerCompletedAt
          ? DateTime.fromISO(row.screenerCompletedAt).toFormat(
              "MM/dd/yyyy hh:mm a",
            )
          : "-",
    },
    {
      key: "tags",
      label: "Tags",
      freezable: true,
      minWidth: 50,
      renderHeader: () =>
        renderRegistryColumnHeader("tags", "Tags", filterContext),
      renderCell: (row) =>
        row?.tags && row.tags.length > 0 ? (
          <Tooltip
            title={`${row.tags.length} tag${row.tags.length !== 1 ? "s" : ""}`}
          >
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleShowTags(row?.tags);
              }}
            >
              <Badge badgeContent={row.tags.length} color="primary">
                <SellIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      key: "notes",
      label: "Notes",
      freezable: true,
      minWidth: 50,
      renderHeader: () =>
        renderRegistryColumnHeader("notes", "Notes", filterContext),
      renderCell: (row) =>
        row?.notesCount > 0 ? (
          <Tooltip
            title={`${row.notesCount} note${row.notesCount !== 1 ? "s" : ""}`}
          >
            <IconButton onClick={() => handleShowNoteDetails(row)}>
              <Badge badgeContent={row.notesCount} color="primary">
                <Message />
              </Badge>
            </IconButton>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      key: "studies",
      label: "Studies",
      freezable: true,
      minWidth: 50,
      renderHeader: () => "Studies",
      renderCell: (row) =>
        row?.studiesCount > 0 ? (
          <Tooltip
            title={`${row.studiesCount} ${
              row.studiesCount !== 1 ? "studies" : "study"
            }`}
          >
            <IconButton onClick={() => handleShowStudyDetails(row)}>
              <Badge badgeContent={row.studiesCount} color="primary">
                <MenuBookIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      key: "lastParticipationDate",
      label: "Last Participation Date",
      freezable: true,
      minWidth: 200,
      renderHeader: () =>
        renderRegistryColumnHeader(
          "lastParticipationDate",
          "Last Participation Date",
          filterContext,
        ),
      renderCell: (row) => row?.lastStudyDate || "-",
    },
    {
      key: "createdOn",
      label: "Created On",
      freezable: true,
      minWidth: 130,
      renderHeader: () =>
        renderRegistryColumnHeader("createdOn", "Created On", filterContext),
      renderCell: (row) => row?.createdAt || "-",
    },
  ];
}

export function getRegistryColumnCellSx(
  column: RegistryParticipantColumnDef,
  frozenKeys: string[],
  frozenKeysInOrder: string[],
  stickyLeft: Record<string, number>,
  isHeader: boolean,
): SxProps {
  const base: SxProps = column.minWidth ? { minWidth: column.minWidth } : {};

  if (column.stickyLeftAlways) {
    return {
      ...base,
      ...StickyStyle,
      left: 0,
      zIndex: isHeader ? 111 : 101,
    };
  }

  if (frozenKeys.includes(column.key)) {
    return {
      ...base,
      ...StickyStyle,
      ...getFrozenCellSx(column.key, frozenKeysInOrder, stickyLeft, isHeader),
    };
  }

  return base;
}

export function getRegistryColumnCellClassName(
  column: RegistryParticipantColumnDef,
  frozenKeys: string[],
  lastLeftPinnedKey: string | null,
): string | undefined {
  if (column.stickyLeftAlways || frozenKeys.includes(column.key)) {
    const classes = ["fixed-col", "left"];
    if (column.key === lastLeftPinnedKey) {
      classes.push("fixed-col-left-edge");
    }
    return classes.join(" ");
  }
  return undefined;
}
